/**
 * KNK-1.52 / FR-42 — Settings → Knock Mode → Bring in existing pins.
 *
 * Preview first, always: the card opens on the server's dry run, and Activate sends back the
 * previewed count, which the server refuses if the history moved since. Each run is listed with
 * Undo while the server says it can still be undone. Rules and copy: `lib/knockActivation.ts`.
 */
import { useCallback, useEffect, useState } from 'react';
import { Loader2, RotateCcw } from 'lucide-react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/components/ui/use-toast';
import {
  ACTIVATION_COPY as COPY,
  canApply,
  previewLines,
  runLine,
  type ActivationPreview,
  type ActivationRun,
} from '@/lib/knockActivation';
import { applyKnockActivation, previewKnockActivation, revertKnockActivation } from '@/services/knockModeService';

const when = (iso: string) =>
  new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });

export function KnockActivationCard() {
  const { toast } = useToast();
  const [preview, setPreview] = useState<ActivationPreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<'preview' | 'apply' | string | null>('preview');
  const [confirming, setConfirming] = useState(false);

  const load = useCallback(async () => {
    setBusy('preview');
    setError(null);
    try {
      setPreview(await previewKnockActivation());
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const fail = (e: unknown) =>
    toast({ title: 'Not changed', description: e instanceof Error ? e.message : String(e), variant: 'destructive' });

  const apply = async () => {
    if (!preview) return;
    setBusy('apply');
    try {
      await applyKnockActivation(preview.knocksImported);
      toast({ title: COPY.applied });
    } catch (e) {
      fail(e);
    } finally {
      // Re-preview either way: after success it shows the run, after a stale refusal the new numbers.
      await load();
    }
  };

  const undo = async (run: ActivationRun) => {
    setBusy(run.id);
    try {
      await revertKnockActivation(run.id);
      toast({ title: COPY.undone });
    } catch (e) {
      fail(e);
    } finally {
      await load();
    }
  };

  return (
    <Card data-testid="knock-activation">
      <CardHeader>
        <CardTitle>{COPY.title}</CardTitle>
        <CardDescription>{COPY.hint}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {busy === 'preview' && !preview && (
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Working out what would change…
          </p>
        )}

        {preview && (
          <>
            <ul className="space-y-1.5 text-sm text-foreground" data-testid="knock-activation-preview">
              {previewLines(preview).map((line) => (
                <li key={line} className="flex gap-2">
                  <span aria-hidden className="text-muted-foreground">•</span>
                  <span>{line}</span>
                </li>
              ))}
            </ul>
            <div className="flex flex-wrap gap-2">
              <Button onClick={() => setConfirming(true)} disabled={busy !== null || !canApply(preview)}>
                {busy === 'apply' && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {COPY.apply}
              </Button>
              <Button variant="outline" onClick={() => void load()} disabled={busy !== null}>
                {busy === 'preview' && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {COPY.preview} again
              </Button>
            </div>

            <div className="space-y-2">
              <p className="text-sm font-medium text-foreground">{COPY.runsTitle}</p>
              {preview.runs.length === 0 ? (
                <p className="text-sm text-muted-foreground">{COPY.noRuns}</p>
              ) : (
                <ul className="divide-y divide-border rounded-md border border-border" data-testid="knock-activation-runs">
                  {preview.runs.map((run) => (
                    <RunRow key={run.id} run={run} busy={busy} onUndo={undo} />
                  ))}
                </ul>
              )}
              <p className="text-xs text-muted-foreground">{COPY.undoHint}</p>
            </div>
          </>
        )}
      </CardContent>

      <AlertDialog open={confirming} onOpenChange={setConfirming}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{COPY.title}?</AlertDialogTitle>
            <AlertDialogDescription>
              {preview && previewLines(preview).join(' ')} The notice to reps cannot be taken back; the import can
              be undone until the first new Knock.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setConfirming(false);
                void apply();
              }}
            >
              {COPY.apply}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}

function RunRow({ run, busy, onUndo }: { run: ActivationRun; busy: string | null; onUndo: (r: ActivationRun) => void }) {
  return (
    <li className="flex flex-col gap-2 p-3 sm:flex-row sm:items-center sm:justify-between" data-testid={`knock-activation-run-${run.id}`}>
      <div className="min-w-0 space-y-0.5">
        <p className="flex flex-wrap items-center gap-2 text-sm text-foreground">
          {when(run.appliedAt)}
          {run.revertedAt && <Badge variant="secondary">Undone</Badge>}
        </p>
        <p className="text-xs text-muted-foreground">{runLine(run)}</p>
        {!run.undoable && !run.revertedAt && run.revertBlocker && (
          <p className="text-xs text-muted-foreground">{run.revertBlocker}</p>
        )}
      </div>
      {run.undoable && (
        <Button size="sm" variant="outline" className="shrink-0 self-start sm:self-center" disabled={busy !== null} onClick={() => onUndo(run)}>
          {busy === run.id ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RotateCcw className="mr-2 h-4 w-4" />}
          {COPY.undo}
        </Button>
      )}
    </li>
  );
}
